import React, { ReactNode, forwardRef, useId } from 'react';

import { Switch as RadixSwitch } from '@radix-ui/themes';
import type { ComponentPropsWithoutRef } from 'react';
import { useThemeContext } from '@radix-ui/themes';
import clsx from 'clsx';

import { HelpText } from './HelpText';
import { Label } from './Label';

type RadixSwitchProps = ComponentPropsWithoutRef<typeof RadixSwitch>;

const sizeMapping = {
  small: '1',
  medium: '2',
  large: '3',
} as const;

function resolveColor(
  color: string,
  accentColor?: string
): RadixSwitchProps['color'] {
  const allowedColors = [
    'gray',
    'blue',
    'red',
    'green',
    'teal',
    'ruby',
    'amber',
    'orange',
    'crimson',
    'jade',
    'pink',
    'plum',
    'purple',
    'violet',
    'indigo',
  ];
  switch (color) {
    case 'primary':
      return allowedColors.includes(accentColor || '')
        ? (accentColor as RadixSwitchProps['color'])
        : 'indigo';
    case 'error':
      return 'crimson';
    case 'success':
      return 'jade';
    case 'warning':
      return 'amber';
    case 'info':
      return 'blue';
    case 'default':
    default:
      return 'gray';
  }
}

const variantMapping = {
  classic: 'classic',
  surface: 'surface',
  soft: 'soft',
  ghost: 'ghost',
} as const;

export interface SwitchProps
  extends Omit<RadixSwitchProps, 'size' | 'color' | 'variant'> {
  label?: ReactNode;
  size?: 'small' | 'medium' | 'large';
  color?: 'default' | 'primary' | 'error' | 'success' | 'warning' | 'info';
  variant?: 'classic' | 'surface' | 'soft' | 'ghost';
  error?: boolean;
  helpText?: string;
  errorMessage?: string;
  className?: string;
}

/**
 * Switch component is a design system adapter for Radix Switch.
 * It supports label, help text, error, size, color, and variant props.
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      label,
      size = 'medium',
      color = 'primary',
      variant = 'surface',
      error = false,
      helpText,
      errorMessage,
      className,
      ...props
    },
    ref
  ) => {
    const inputId = useId();
    const mappedSize = sizeMapping[size] || sizeMapping.medium;
    const theme = useThemeContext();
    const accentColor = theme?.accentColor || 'indigo';
    const mappedColor = error
      ? 'crimson'
      : (resolveColor(String(color), accentColor) as RadixSwitchProps['color']);
    const mappedVariant = variantMapping[variant] || variantMapping.surface;

    return (
      <div className={clsx('switch-wrapper', className)}>
        {label && (
          <Label htmlFor={inputId} className={clsx({ error })}>
            {label}
          </Label>
        )}
        <RadixSwitch
          id={inputId}
          size={mappedSize as RadixSwitchProps['size']}
          color={mappedColor as RadixSwitchProps['color']}
          variant={mappedVariant as RadixSwitchProps['variant']}
          ref={ref}
          className={clsx(
            `switch-size-${size}`,
            `switch-color-${color}`,
            `switch-variant-${variant}`,
            { error },
            'switch-root'
          )}
          data-error={error}
          {...props}
        />
        {(errorMessage || helpText) && (
          <HelpText error={error}>{error ? errorMessage : helpText}</HelpText>
        )}
      </div>
    );
  }
);

Switch.displayName = 'Switch';
